import * as React from 'react'
import { mergeClasses, Tooltip } from '@fluentui/react-components'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import { formatTimeInMinutes } from '../../../../helpers/numberHelper'
import CalendarTotals from './CalendarTotals'
import { useTimeCalendarStyles } from './timeCalendarStyles'
import { DailyTotalDto, DayColor } from './timeCalendarHelper'

type Props = {
    date: DateTime
    total?: DailyTotalDto
    color: DayColor
    compact: boolean
    onSelectDate: (date: string) => void
}

export function colorClassName(color: DayColor, styles: ReturnType<typeof useTimeCalendarStyles>): string | undefined {
    switch (color) {
        case DayColor.Red:
            return styles.red
        case DayColor.Orange:
            return styles.orange
        case DayColor.Green:
            return styles.green
        default:
            return undefined
    }
}

/**
 * A single day cell. Clicking it filters the overview on that day, which also closes the calendar.
 */
export default function CalendarCell(props: Props) {
    const styles = useTimeCalendarStyles()
    const { t: translate, i18n } = useTranslation()

    const isoDate = props.date.toISODate()
    // Format with the language the user picked in the app, not the browser locale luxon defaults to.
    const fullDate = props.date.setLocale(i18n.language).toFormat('DD')
    const isToday = isoDate === DateTime.local().toISODate()
    const minutes = props.total?.duration ?? 0

    return (
        <td className={mergeClasses(styles.cell, props.compact && styles.cellCompact)}>
            <Tooltip withArrow relationship={'label'} content={<CalendarTotals title={fullDate} days={props.total ? [props.total] : []} />}>
                <button
                    type={'button'}
                    aria-label={translate('time.calendar.day.select', { date: fullDate })}
                    className={mergeClasses(styles.dayButton, colorClassName(props.color, styles), isToday && styles.today)}
                    onClick={() => props.onSelectDate(isoDate)}
                >
                    {formatTimeInMinutes(minutes)}
                </button>
            </Tooltip>
        </td>
    )
}
